import React from 'react';
import {
  Square3Stack3DIcon,
  PhotoIcon,
  CpuChipIcon,
  ArrowPathIcon,
  GlobeAltIcon,
  ChartBarIcon,
} from '@heroicons/react/24/outline';

const features = [
  {
    icon: Square3Stack3DIcon,
    title: 'Gang Sheet Engine',
    subtitle: 'Zero waste, maximum profit',
    description:
      'DPI-aware auto-layout arranges designs to fill every inch. Printer-specific constraints, intelligent spacing, print-ready PDF export.',
    gradient: 'from-mint-100 to-mint-200',
    iconColor: 'text-mint-600',
    stat: '40%',
    statLabel: 'less material waste',
  },
  {
    icon: PhotoIcon,
    title: 'Mockup Generator',
    subtitle: 'Pro photos in seconds',
    description:
      'Intelligent masking produces mockups where your design wraps and shades realistically. Batch process entire collections.',
    gradient: 'from-lavender-100 to-lavender-200',
    iconColor: 'text-lavender-600',
    stat: '10x',
    statLabel: 'faster than Photoshop',
  },
  {
    icon: CpuChipIcon,
    title: 'AI Product Tagging',
    subtitle: 'Upload and forget',
    description:
      'Computer vision detects subjects, styles, and themes in every design. Search your library by tag, generate optimized listings automatically.',
    gradient: 'from-rose-100 to-rose-200',
    iconColor: 'text-rose-500',
    stat: '100%',
    statLabel: 'hands-free tagging',
  },
  {
    icon: ArrowPathIcon,
    title: 'Multi-Platform Sync',
    subtitle: 'Etsy + Shopify + your store',
    description:
      'Connect your Etsy and Shopify shops. Sync listings, orders, and inventory across all channels in real-time.',
    gradient: 'from-peach-100 to-peach-200',
    iconColor: 'text-peach-600',
    stat: '3',
    statLabel: 'sales channels',
  },
  {
    icon: GlobeAltIcon,
    title: 'Branded Storefront',
    subtitle: 'Your own commerce site',
    description:
      'Launch a CraftFlow Commerce storefront with custom domain, Stripe checkout, CMS builder, and customer accounts.',
    gradient: 'from-sage-100 to-sage-200',
    iconColor: 'text-sage-600',
    stat: '0',
    statLabel: 'code required',
  },
  {
    icon: ChartBarIcon,
    title: 'Analytics & Automation',
    subtitle: 'Data-driven decisions',
    description:
      'Track sales, orders, and customer behavior across all channels. Set up automated workflows for production and fulfillment.',
    gradient: 'from-amber-100 to-amber-200',
    iconColor: 'text-amber-600',
    stat: '24/7',
    statLabel: 'automated workflows',
  },
];

const FeaturesSection = () => {
  return (
    <section id="features" className="py-16 md:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-14">
          <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-4">
            Everything you need to run a print business
          </h2>
          <p className="text-lg text-gray-500 max-w-2xl mx-auto">
            From design upload to shipped order — automate every step of your print-on-demand workflow.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map(feature => (
            <div
              key={feature.title}
              className="relative bg-white rounded-2xl border border-gray-100 p-8 hover:shadow-lg hover:border-gray-200 transition-all duration-300 group"
            >
              <div
                className={`w-12 h-12 rounded-xl bg-gradient-to-br ${feature.gradient} flex items-center justify-center mb-5`}
              >
                <feature.icon className={`w-6 h-6 ${feature.iconColor}`} aria-hidden="true" />
              </div>

              <h3 className="text-lg font-bold text-gray-900 mb-1">{feature.title}</h3>
              <p className="text-sm font-medium text-gray-400 mb-3">{feature.subtitle}</p>
              <p className="text-sm text-gray-500 leading-relaxed mb-5">{feature.description}</p>

              <div className="flex items-baseline gap-2 pt-4 border-t border-gray-100">
                <span className="text-2xl font-extrabold text-gray-900">{feature.stat}</span>
                <span className="text-sm text-gray-400">{feature.statLabel}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FeaturesSection;
